import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import Cookies from "js-cookie";
import Select from "react-select";
import GlobalLoader from "./GlobalLoader";
import "../../styles/common/dashboardPage.scss";
import "../../styles/common/dashboardOverviewSection.scss";
import DashboardInstitutionSection from "./DashboardInstitutionSection";
import DashboardCourseSection from "./DashboardCourseSection";
import DashboardBatchSection from "./DashboardBatchSection";
import DashboardTeacherSection from "./DashboardTeacherSection";
import CommonTable from "./commonTable";
import TooltipText from "./tooltipText";
import ChartCard from "./charts/ChartCard";
import BarChart from "./charts/BarChart";
import DoughnutChart from "./charts/DoughnutChart";
import LineChart from "./charts/LineChart";
import AreaChart from "./charts/AreaChart";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  getAdminDashboard1,
  getAdminDashboard2,
  getAdminDashboard3,
  getAdminDashboard4,
  getAdminDashboard6,
  getAdminDashboard7,
  getAdminDashboard8,
  getAdminDashboard9,
} from "../../helper/apicalls/dashboard";

const DashboardPage = () => {
  const outletContext = useOutletContext?.() || {};
  const { setHeaderName, setIsShowSearch, setReportBreadcrumbLinks } =
    outletContext;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Drill-down navigation state (selection objects). The current view is derived from the URL.
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetailTab, setBatchDetailTab] = useState("teachers"); // teachers | students

  // Students (users) state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentData, setStudentData] = useState(null);

  const [activeAdminSection, setActiveAdminSection] = useState("institution");

  const [overallData, setOverallData] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState("");
  const [institutionData, setInstitutionData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourseKey, setSelectedCourseKey] = useState("");
  const [courseData, setCourseData] = useState(null);

  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [batchData, setBatchData] = useState(null);

  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [teacherData, setTeacherData] = useState(null);

  const role = Cookies.get("role");

  const lastTeacherFetchRef = useRef({ teacherId: "", courseKey: "" });
  const lastInstitutionFetchRef = useRef("");
  const lastCourseFetchRef = useRef("");
  const lastBatchFetchRef = useRef("");

  const institutionIdParam = params?.institutionId
    ? String(params.institutionId)
    : "";
  const courseKeyParam = params?.courseKey ? String(params.courseKey) : "";
  const batchIdParam = params?.batchId ? String(params.batchId) : "";
  const studentIdParam = params?.studentId ? String(params.studentId) : "";
  const teacherIdParam = params?.teacherId ? String(params.teacherId) : "";

  const viewLevel = useMemo(() => {
    if (teacherIdParam) return "teacherReports";
    if (studentIdParam) return "studentReports";
    if (batchIdParam && location.pathname.includes("/reports")) {
      if (
        location.pathname.includes("/batch/") &&
        location.pathname.endsWith("/reports")
      ) {
        return "batchReports";
      }
      return "batchDetail";
    }
    if (courseKeyParam && location.pathname.endsWith("/batches"))
      return "batches";
    if (courseKeyParam) return "courseReports";
    if (institutionIdParam) return "courses";
    return "institutions";
  }, [
    batchIdParam,
    courseKeyParam,
    institutionIdParam,
    location.pathname,
    studentIdParam,
    teacherIdParam,
  ]);

  useEffect(() => {
    if (role === "admin") {
      if (typeof setHeaderName === "function") {
        setHeaderName("Reports");
      }
      if (typeof setIsShowSearch === "function") {
        setIsShowSearch(false);
      }
    }
  }, [role, setHeaderName, setIsShowSearch]);

  useEffect(() => {
    if (role !== "admin" || typeof setReportBreadcrumbLinks !== "function") {
      return;
    }

    const links = [];

    // Reports home
    links.push({
      label: "Institutions",
      path: "/admin/reports",
      active: viewLevel === "institutions",
    });

    if (selectedInstitution) {
      links.push({
        label: selectedInstitution.name || "Institution",
        path: institutionIdParam
          ? `/admin/reports/institution/${institutionIdParam}`
          : "/admin/reports",
        active: viewLevel === "courses",
      });
    }

    if (selectedCourse) {
      links.push({
        label: selectedCourse.name || "Course",
        path:
          institutionIdParam && courseKeyParam
            ? `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}`
            : "/admin/reports",
        active: viewLevel === "courseReports",
      });
    }

    if (viewLevel === "batches") {
      links.push({
        label: "Batches",
        path:
          institutionIdParam && courseKeyParam
            ? `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batches`
            : "/admin/reports",
        active: true,
      });
    }

    if (selectedBatch) {
      links.push({
        label: selectedBatch.name || `Batch ${selectedBatch.id}`,
        path:
          institutionIdParam && courseKeyParam && batchIdParam
            ? `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchIdParam}`
            : "/admin/reports",
        active: viewLevel === "batchDetail" || viewLevel === "batchReports",
      });
    }

    if (viewLevel === "batchReports" && selectedBatch) {
      links.push({
        label: "Reports",
        path:
          institutionIdParam && courseKeyParam && batchIdParam
            ? `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchIdParam}/reports`
            : "/admin/reports",
        active: true,
      });
    }

    if (viewLevel === "teacherReports" && selectedTeacher) {
      links.push({
        label:
          selectedTeacher.name ||
          selectedTeacher.teacher_name ||
          selectedTeacher.full_name ||
          selectedTeacher.email ||
          "Teacher",
        path:
          institutionIdParam && courseKeyParam && batchIdParam && teacherIdParam
            ? `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchIdParam}/teacher/${teacherIdParam}`
            : "/admin/reports",
        active: true,
      });
    }

    if (viewLevel === "studentReports" && selectedStudent) {
      links.push({
        label:
          selectedStudent.name ||
          selectedStudent.full_name ||
          selectedStudent.email ||
          `Student ${selectedStudent.id}`,
        path:
          institutionIdParam && courseKeyParam && batchIdParam && studentIdParam
            ? `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchIdParam}/student/${studentIdParam}`
            : "/admin/reports",
        active: true,
      });
    }

    setReportBreadcrumbLinks(links);
  }, [
    role,
    setReportBreadcrumbLinks,
    institutionIdParam,
    courseKeyParam,
    batchIdParam,
    studentIdParam,
    teacherIdParam,
    selectedInstitution,
    selectedCourse,
    selectedBatch,
    selectedTeacher,
    selectedStudent,
    viewLevel,
  ]);

  useEffect(() => {
    if (role !== "admin") {
      setOverallData(null);
      setInstitutions([]);
      setSelectedInstitutionId("");
      setInstitutionData(null);
      setCourses([]);
      setSelectedCourseKey("");
      setCourseData(null);
      setSelectedBatchId("");
      setBatchData(null);
      setTeachers([]);
      setSelectedTeacherId("");
      setTeacherData(null);
      setActiveAdminSection("institution");
      lastTeacherFetchRef.current = { teacherId: "", courseKey: "" };
      lastInstitutionFetchRef.current = "";
      lastCourseFetchRef.current = "";
      lastBatchFetchRef.current = "";
    }
  }, [role]);

  // Sync selected IDs from route params.
  useEffect(() => {
    if (role !== "admin") return;
    setSelectedInstitutionId(institutionIdParam);
    setSelectedCourseKey(courseKeyParam);
    setSelectedBatchId(batchIdParam);
    setSelectedStudentId(studentIdParam);
    setSelectedTeacherId(teacherIdParam);
  }, [
    role,
    institutionIdParam,
    courseKeyParam,
    batchIdParam,
    studentIdParam,
    teacherIdParam,
  ]);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (role !== "admin") return;
      if (overallData && institutions.length > 0) return;

      const token = Cookies.get("admin_token");
      if (!token) return;

      setLoading(true);
      setError("");
      try {
        const res = await getAdminDashboard1(token);
        setOverallData(res || null);

        const allInstitutions = res?.["all institutions"]?.institutions || [];
        setInstitutions(allInstitutions);
      } catch (e) {
        console.error("Failed to load admin dashboard data", e);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [role, overallData, institutions.length]);

  // Keep selected objects in sync with loaded lists (for breadcrumb labels).
  useEffect(() => {
    if (!institutionIdParam) {
      setSelectedInstitution(null);
      return;
    }
    const found = institutions.find(
      (i) => String(i?.id) === String(institutionIdParam),
    );
    if (found) setSelectedInstitution(found);
  }, [institutionIdParam, institutions]);

  useEffect(() => {
    if (!courseKeyParam) {
      setSelectedCourse(null);
      return;
    }
    const found = courses.find(
      (c) => String(c?.key) === String(courseKeyParam),
    );
    if (found) setSelectedCourse(found);
  }, [courseKeyParam, courses]);

  useEffect(() => {
    if (!batchIdParam) {
      setSelectedBatch(null);
      return;
    }
    const list = Array.isArray(courseData?.batches) ? courseData.batches : [];
    const found = list.find(
      (b) =>
        String(b?.id ?? b?.batch_id ?? b?.batchId) === String(batchIdParam),
    );
    if (found) setSelectedBatch(found);
  }, [batchIdParam, courseData]);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (role !== "admin") {
        setTeacherData(null);
        return;
      }

      const token = Cookies.get("admin_token");
      if (!token) return;

      if (!selectedTeacherId) {
        setTeacherData(null);
        return;
      }

      const last = lastTeacherFetchRef.current;
      const nextCourseKey = selectedCourseKey || "";
      if (
        last.teacherId === selectedTeacherId &&
        last.courseKey === nextCourseKey
      ) {
        return;
      }

      lastTeacherFetchRef.current = {
        teacherId: selectedTeacherId,
        courseKey: nextCourseKey,
      };

      setLoading(true);
      setError("");
      try {
        const res = await getAdminDashboard6(
          token,
          selectedTeacherId,
          nextCourseKey,
        );
        setTeacherData(res || null);
      } catch (e) {
        console.error("Failed to load teacher dashboard data", e);
        setError("Failed to load teacher data");
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [selectedTeacherId, selectedCourseKey, role]);

  useEffect(() => {
    const fetchInstitution = async () => {
      if (!selectedInstitutionId || role !== "admin") {
        setInstitutionData(null);
        setCourses([]);
        setSelectedCourseKey("");
        setCourseData(null);
        setSelectedBatchId("");
        setBatchData(null);
        lastInstitutionFetchRef.current = "";
        return;
      }

      const token = Cookies.get("admin_token");
      if (!token) return;

      if (lastInstitutionFetchRef.current === selectedInstitutionId) {
        return;
      }

      lastInstitutionFetchRef.current = selectedInstitutionId;

      setLoading(true);
      setError("");
      try {
        const res = await getAdminDashboard2(token, selectedInstitutionId);
        setInstitutionData(res || null);

        const instCourses = res?.lists?.courses || [];
        setCourses(instCourses);
      } catch (e) {
        console.error("Failed to load institution dashboard data", e);
        setError("Failed to load institution data");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitution();
  }, [selectedInstitutionId, role]);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!selectedCourseKey || role !== "admin") {
        setCourseData(null);
        setSelectedBatchId("");
        setBatchData(null);
        lastCourseFetchRef.current = "";
        return;
      }

      const token = Cookies.get("admin_token");
      if (!token) return;

      if (lastCourseFetchRef.current === `${selectedCourseKey}_${institutionIdParam}`) {
        return;
      }

      lastCourseFetchRef.current = `${selectedCourseKey}_${institutionIdParam}`;

      setLoading(true);
      setError("");
      try {
        const res = await getAdminDashboard3(token, selectedCourseKey, institutionIdParam);
        setCourseData(res || null);
      } catch (e) {
        console.error("Failed to load course dashboard data", e);
        setError("Failed to load course data");
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [selectedCourseKey, institutionIdParam, role]);

  useEffect(() => {
    const fetchBatch = async () => {
      if (!selectedBatchId || role !== "admin") {
        setBatchData(null);
        lastBatchFetchRef.current = "";
        return;
      }

      const token = Cookies.get("admin_token");
      if (!token) return;

      if (lastBatchFetchRef.current === selectedBatchId) {
        return;
      }

      lastBatchFetchRef.current = selectedBatchId;

      setLoading(true);
      setError("");
      try {
        const batchRes = await getAdminDashboard4(token, selectedBatchId);
        setBatchData(batchRes || null);

        const teacherListRaw =
          batchRes?.teachers ||
          batchRes?.teacher_list ||
          batchRes?.lists?.teachers ||
          batchRes?.data?.teachers ||
          batchRes?.data ||
          [];

        const normalizedTeachers = Array.isArray(teacherListRaw)
          ? Array.isArray(teacherListRaw[0])
            ? teacherListRaw[0]
            : teacherListRaw
          : [];

        setTeachers(normalizedTeachers);
      } catch (e) {
        console.error("Failed to load batch dashboard data", e);
        setError("Failed to load batch data");
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [selectedBatchId, role]);

  // Drill-down navigation handlers
  const handleInstitutionClick = (institution) => {
    const instId = institution?.id;
    setSelectedInstitution(institution);
    setSelectedInstitutionId(instId);
    navigate(`/admin/reports/institution/${instId}`);
    const token = Cookies.get("admin_token");
    if (token) {
      setLoading(true);
      lastInstitutionFetchRef.current = institution.id;
      getAdminDashboard2(token, institution.id)
        .then((res) => {
          setInstitutionData(res || null);
          setCourses(res?.lists?.courses || []);
        })
        .catch((e) => console.error("Failed to load institution data", e))
        .finally(() => setLoading(false));
    }
  };

  const handleCourseClick = (course) => {
    setSelectedCourse(course);
    setSelectedCourseKey(course.key);
    navigate(
      `/admin/reports/institution/${institutionIdParam}/course/${course.key}`,
    );
    const token = Cookies.get("admin_token");
    if (token) {
      setLoading(true);
      lastCourseFetchRef.current = `${course.key}_${institutionIdParam}`;
      getAdminDashboard3(token, course.key, institutionIdParam)
        .then((res) => setCourseData(res || null))
        .catch((e) => console.error("Failed to load course data", e))
        .finally(() => setLoading(false));
    }
  };

  const handleCourseCellClick = (course) => {
    setSelectedCourse(course);
    setSelectedCourseKey(course.key);
    navigate(
      `/admin/reports/institution/${institutionIdParam}/course/${course.key}/batches`,
    );
    const token = Cookies.get("admin_token");
    if (token) {
      setLoading(true);
      lastCourseFetchRef.current = `${course.key}_${institutionIdParam}`;
      getAdminDashboard3(token, course.key, institutionIdParam)
        .then((res) => setCourseData(res || null))
        .catch((e) => console.error("Failed to load course data", e))
        .finally(() => setLoading(false));
    }
  };

  // Click "View Reports" button on batch - shows charts/cards
  const handleBatchViewReports = (batch) => {
    setSelectedBatch(batch);
    const batchId = batch?.id ?? batch?.batch_id ?? batch?.batchId ?? "";
    setSelectedBatchId(String(batchId));
    navigate(
      `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchId}/reports`,
    );
    const token = Cookies.get("admin_token");
    if (token && batchId) {
      setLoading(true);
      lastBatchFetchRef.current = String(batchId);
      getAdminDashboard4(token, String(batchId))
        .then((res) => {
          setBatchData(res || null);
          const teacherListRaw =
            res?.teachers ||
            res?.teacher_list ||
            res?.lists?.teachers ||
            res?.data?.teachers ||
            res?.data ||
            [];
          const normalizedTeachers = Array.isArray(teacherListRaw)
            ? Array.isArray(teacherListRaw[0])
              ? teacherListRaw[0]
              : teacherListRaw
            : [];
          setTeachers(normalizedTeachers);
        })
        .catch((e) => console.error("Failed to load batch data", e))
        .finally(() => setLoading(false));
    }
  };

  // Click batch row/cell - shows Teachers/Students tabs with tables
  const handleBatchRowClick = (batch) => {
    setSelectedBatch(batch);
    const batchId = batch?.id ?? batch?.batch_id ?? batch?.batchId ?? "";
    setSelectedBatchId(String(batchId));
    navigate(
      `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchId}`,
    );
    const token = Cookies.get("admin_token");
    if (token && batchId) {
      setLoading(true);
      lastBatchFetchRef.current = String(batchId);
      getAdminDashboard4(token, String(batchId))
        .then((res) => {
          setBatchData(res || null);
          const teacherListRaw =
            res?.teachers ||
            res?.teacher_list ||
            res?.lists?.teachers ||
            res?.data?.teachers ||
            res?.data ||
            [];
          const normalizedTeachers = Array.isArray(teacherListRaw)
            ? Array.isArray(teacherListRaw[0])
              ? teacherListRaw[0]
              : teacherListRaw
            : [];
          setTeachers(normalizedTeachers);
        })
        .catch((e) => console.error("Failed to load batch data", e))
        .finally(() => setLoading(false));
    }
  };

  // Fetch students when Students tab is clicked
  useEffect(() => {
    const fetchStudents = async () => {
      if (
        viewLevel !== "batchDetail" ||
        batchDetailTab !== "students" ||
        !selectedBatchId
      ) {
        return;
      }

      const token = Cookies.get("admin_token");
      if (!token) return;

      setLoading(true);
      setError("");
      try {
        const res = await getAdminDashboard7(token, selectedBatchId);
        const studentsList = res?.users || res?.data || res?.students || [];
        setStudents(studentsList);
      } catch (e) {
        console.error("Failed to load students data", e);
        setError("Failed to load students data");
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [viewLevel, batchDetailTab, selectedBatchId]);

  // Click student row - shows student/user reports
  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    const studentId =
      student?.id ?? student?.user_id ?? student?.student_id ?? "";
    setSelectedStudentId(String(studentId));
    navigate(
      `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchIdParam}/student/${studentId}`,
    );
    const token = Cookies.get("admin_token");
    if (token && studentId) {
      setLoading(true);
      Promise.all([
        getAdminDashboard8(token, String(studentId)),
        getAdminDashboard9(token, String(studentId)),
      ])
        .then(([overviewRes, analyticsRes]) => {
          setStudentData({
            overview: overviewRes || null,
            analytics: analyticsRes || null,
          });
        })
        .catch((e) => console.error("Failed to load student data", e))
        .finally(() => setLoading(false));
    }
  };

  // Click teacher row/button - shows teacher reports
  const handleTeacherClick = (teacher) => {
    setSelectedTeacher(teacher);
    const teacherId =
      teacher?.id ?? teacher?.teacher_id ?? teacher?.teacherId ?? "";
    setSelectedTeacherId(String(teacherId));
    navigate(
      `/admin/reports/institution/${institutionIdParam}/course/${courseKeyParam}/batch/${batchIdParam}/teacher/${teacherId}`,
    );
    const token = Cookies.get("admin_token");
    if (token && teacherId) {
      setLoading(true);
      lastTeacherFetchRef.current = {
        teacherId: String(teacherId),
        courseKey: selectedCourseKey || "",
      };
      getAdminDashboard6(token, String(teacherId), selectedCourseKey || "")
        .then((res) => {
          setTeacherData(res || null);
        })
        .catch((e) => console.error("Failed to load teacher data", e))
        .finally(() => setLoading(false));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const title = role === "admin" ? "Reports" : "Dashboard";

  const courseColumns = [
    { field: "name", label: "Course Name", align: "left" },
    {
      field: "publish",
      label: "Status",
      align: "left",
      render: (v, row) => (row?.publish ? "Published" : "Not Published"),
    },
    // { field: "key", label: "Course Key", align: "left" },
    {
      field: "actions",
      label: "Actions",
      align: "center",
      render: (_, row) => (
        <button
          type="button"
          className="dashboard-view-reports-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleCourseClick(row);
          }}
        >
          <AssessmentIcon fontSize="small" />
          View Reports
        </button>
      ),
    },
  ];

  const batchColumns = [
    {
      field: "name",
      label: "Batch Name",
      align: "left",
      render: (v, row) =>
        v || row.batch_name || `Batch ${row.id || row.batch_id || row.batchId}`,
    },
    {
      field: "active_users",
      label: "Active Users",
      align: "left",
      render: (v, row) =>
        v ??
        row.active_users ??
        row.activeUsers ??
        row.total_active_users ??
        row.totalActiveUsers ??
        "--",
    },
    {
      field: "inactive_users",
      label: "Inactive Users",
      align: "left",
      render: (v, row) =>
        v ??
        row.inactive_users ??
        row.inactiveUsers ??
        row.total_inactive_users ??
        row.totalInactiveUsers ??
        "--",
    },
    {
      field: "total_teachers",
      label: "Total Teachers",
      align: "left",
      render: (v, row) =>
        v ??
        row.total_teachers ??
        row.totalTeachers ??
        row.teacher_count ??
        row.total_teacher ??
        "--",
    },
    {
      field: "actions",
      label: "Actions",
      align: "center",
      render: (_, row) => (
        <button
          type="button"
          className="dashboard-view-reports-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleBatchViewReports(row);
          }}
        >
          <AssessmentIcon fontSize="small" />
          View Reports
        </button>
      ),
    },
  ];

  const studentColumns = [
    {
      field: "name",
      label: "Student Name",
      align: "left",
      render: (v, row) =>
        v || row.full_name || row.username || row.email || "--",
    },
    {
      field: "email",
      label: "Email",
      align: "left",
      render: (v, row) => v || row.email || "--",
    },
    {
      field: "grade",
      label: "Grade",
      align: "left",
      render: (v, row) =>
        v ??
        row.grade ??
        row.class ??
        row.standard ??
        row.student_grade ??
        row.studentGrade ??
        "--",
    },
    {
      field: "actions",
      label: "Actions",
      align: "center",
      render: (_, row) => (
        <button
          type="button"
          className="dashboard-view-reports-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleStudentClick(row);
          }}
        >
          <AssessmentIcon fontSize="small" />
          View Reports
        </button>
      ),
    },
  ];

  const teacherColumns = [
    {
      field: "name",
      label: "Teacher Name",
      align: "left",
      render: (v, row) =>
        v || row.teacher_name || row.full_name || row.email || "--",
    },
    {
      field: "email",
      label: "Email",
      align: "left",
      render: (v, row) => v || row.teacher_email || "--",
    },
    // { field: "phone", label: "Phone", align: "left", render: (v, row) => v || row.phone || row.teacher_phone || "--" },
    {
      field: "actions",
      label: "Actions",
      align: "center",
      render: (_, row) => (
        <button
          type="button"
          className="dashboard-view-reports-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleTeacherClick(row);
          }}
        >
          <AssessmentIcon fontSize="small" />
          View Reports
        </button>
      ),
    },
  ];

  const batchesList = useMemo(
    () => (Array.isArray(courseData?.batches) ? courseData.batches : []),
    [courseData],
  );

  const studentAnalytics = studentData?.analytics || null;

  const chartPalette = useMemo(
    () => ({
      base: {
        orange: "#FCA43B",
        mint: "#85EEAD",
        lime: "#D4F92D",
        yellow: "#FAEE70",
      },
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
      accentsHover: [
        "rgba(252, 164, 59, 0.95)",
        "rgba(133, 238, 173, 0.95)",
        "rgba(212, 249, 45, 0.95)",
        "rgba(250, 238, 112, 0.95)",
        "rgba(148, 163, 184, 0.85)",
      ],
    }),
    [],
  );

  const safePct = (num, den) => {
    const n = Number(num) || 0;
    const d = Number(den) || 0;
    if (!d) return 0;
    const raw = (n / d) * 100;
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(100, raw));
  };

  const buildSubjectList = (obj) => {
    if (!obj || typeof obj !== "object") return [];
    return Object.keys(obj)
      .filter((k) => k !== "overall")
      .map((key) => ({
        key,
        name: obj[key]?.name || key,
        ...obj[key],
      }));
  };

  return (
    <div className="dashboard-page">
      {loading && <GlobalLoader visible={loading} text="Loading..." />}
      {!loading && error && <p>{error}</p>}
      {!loading && !error && (
        <>
          {role === "admin" && (
            <>
              {viewLevel === "institutions" && (
                <section className="dashboard-overview-section">
                  <h2>Institutions</h2>
                  <div className="overview-grid">
                    {institutions.map((inst) => {
                      const name = inst.name || "Unnamed Institution";
                      const type = inst.type || "Institution";
                      const teachers =
                        inst.teacher_count ?? inst.total_teachers ?? null;
                      const batches =
                        inst.batch_count ?? inst.total_batches ?? null;

                      return (
                        <div
                          key={inst.id}
                          className="card overview-card institution-card"
                          onClick={() => handleInstitutionClick(inst)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              handleInstitutionClick(inst);
                          }}
                        >
                          <div className="overview-card-content">
                            <div className="overview-card-text">
                              <TooltipText
                                text={name}
                                className="overview-card-label"
                                fullWidth
                              />
                              <div className="overview-card-subtext">
                                <span>{type}</span>
                                {teachers != null && (
                                  <span> · {teachers} Teachers</span>
                                )}
                                {batches != null && (
                                  <span> · {batches} Batches</span>
                                )}
                              </div>
                            </div>
                            <SchoolOutlinedIcon
                              className="overview-card-icon"
                              sx={{ color: "#FCA43B !important" }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
              {viewLevel === "courses" && selectedInstitution && (
                <section className="dashboard-section">
                  {/* <h2>Courses - {selectedInstitution.name}</h2> */}
                  <CommonTable
                    data={courses}
                    columns={courseColumns}
                    getRowId={(row) => row.key}
                    onRowClick={handleCourseCellClick}
                    emptyText="No courses available"
                    showToolbar={true}
                    title="Courses"
                    showSearch={true}
                    showFilter={false}
                  />
                </section>
              )}
              {viewLevel === "courseReports" && selectedCourse && (
                <>
                  <DashboardCourseSection
                    courseOptions={[]}
                    selectedCourseOption={{
                      value: selectedCourse.key,
                      label: selectedCourse.name,
                    }}
                    onChangeCourse={() => {}}
                    courses={[selectedCourse]}
                    courseData={courseData}
                    stats={courseData?.stats}
                    overall={courseData?.overall}
                    batches={courseData?.batches}
                    showSelectRow={false}
                  />
                </>
              )}
              {viewLevel === "batches" && selectedCourse && (
                <section className="dashboard-section">
                  {/* <h2>Batches - {selectedCourse.name}</h2> */}
                  <CommonTable
                    data={batchesList}
                    columns={batchColumns}
                    getRowId={(row) =>
                      row?.id ?? row?.batch_id ?? row?.batchId ?? Math.random()
                    }
                    onRowClick={handleBatchRowClick}
                    emptyText="No batches available"
                    showToolbar={true}
                    title="Batches"
                    showSearch={true}
                    showFilter={false}
                  />
                </section>
              )}
              {viewLevel === "batchReports" && selectedBatch && (
                <DashboardBatchSection
                  batchOptions={[]}
                  selectedBatchOption={{
                    value:
                      selectedBatch?.id ??
                      selectedBatch?.batch_id ??
                      selectedBatch?.batchId ??
                      "",
                    label: selectedBatch?.name ?? `Batch ${selectedBatch?.id}`,
                  }}
                  onChangeBatch={() => {}}
                  batches={[selectedBatch]}
                  batchData={batchData}
                  showSelectRow={false}
                />
              )}
              {viewLevel === "batchDetail" && selectedBatch && (
                <section className="dashboard-section">
                  {/* <h2>{selectedBatch.name || `Batch ${selectedBatch.id}`} Details</h2> */}
                  <div className="dashboard-sticky-tabs">
                    <div className="dashboard-filter-tabs-wrap">
                      <div className="dashboard-filter-tabs">
                        <button
                          type="button"
                          className={`dashboard-filter-tab ${batchDetailTab === "teachers" ? "active" : ""}`}
                          onClick={() => setBatchDetailTab("teachers")}
                        >
                          <PersonOutlineOutlinedIcon
                            fontSize="small"
                            style={{ marginRight: 6 }}
                          />
                          Teachers
                        </button>
                        <button
                          type="button"
                          className={`dashboard-filter-tab ${batchDetailTab === "students" ? "active" : ""}`}
                          onClick={() => setBatchDetailTab("students")}
                        >
                          <GroupsOutlinedIcon
                            fontSize="small"
                            style={{ marginRight: 6 }}
                          />
                          Students
                        </button>
                      </div>
                    </div>
                  </div>
                  {batchDetailTab === "teachers" && (
                    <div className="dashboard-section">
                      <CommonTable
                        data={teachers}
                        columns={[
                          {
                            field: "name",
                            label: "Name",
                            align: "left",
                            render: (v, row) =>
                              v ||
                              row.teacher_name ||
                              row.full_name ||
                              row.email ||
                              "--",
                          },
                          {
                            field: "email",
                            label: "Email",
                            align: "left",
                            render: (v, row) => v || row.teacher_email || "--",
                          },
                          {
                            field: "phone",
                            label: "Phone",
                            align: "left",
                            render: (v, row) =>
                              v || row.phone || row.teacher_phone || "--",
                          },
                          {
                            field: "actions",
                            label: "Actions",
                            align: "center",
                            render: (_, row) => (
                              <button
                                type="button"
                                className="dashboard-view-reports-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTeacherClick(row);
                                }}
                              >
                                <AssessmentIcon fontSize="small" />
                                View Reports
                              </button>
                            ),
                          },
                        ]}
                        getRowId={(row) =>
                          row.id ||
                          row.teacher_id ||
                          row.teacherId ||
                          Math.random()
                        }
                        emptyText="No teachers in this batch"
                        showToolbar={true}
                        title="Teachers"
                        showSearch={true}
                        showFilter={false}
                      />
                    </div>
                  )}
                  {batchDetailTab === "students" && (
                    <div className="dashboard-section">
                      <CommonTable
                        data={students}
                        columns={studentColumns}
                        getRowId={(row) =>
                          row.id ||
                          row.user_id ||
                          row.student_id ||
                          Math.random()
                        }
                        onRowClick={handleStudentClick}
                        emptyText="No students in this batch"
                        showToolbar={true}
                        title="Students"
                        showSearch={true}
                        showFilter={false}
                      />
                    </div>
                  )}
                </section>
              )}
              {viewLevel === "teacherReports" && selectedTeacher && (
                <DashboardTeacherSection
                  teacherOptions={[]}
                  selectedTeacherOption={{
                    value:
                      selectedTeacher?.id ??
                      selectedTeacher?.teacher_id ??
                      selectedTeacher?.teacherId ??
                      "",
                    label:
                      selectedTeacher?.name ||
                      selectedTeacher?.teacher_name ||
                      selectedTeacher?.full_name ||
                      selectedTeacher?.email ||
                      "Selected Teacher",
                  }}
                  onChangeTeacher={() => {}}
                  teachers={teachers}
                  teacherData={teacherData}
                  showSelectRow={false}
                />
              )}
              {viewLevel === "studentReports" && selectedStudent && (
                <section className="dashboard-section">
                  {/* <h2>{selectedStudent.name || selectedStudent.full_name || `Student ${selectedStudent.id}`} - Reports</h2> */}

                  <section className="dashboard-overview-section">
                    <h3>Performance Summary</h3>
                    <div className="overview-grid">
                      <div className="card overview-card">
                        <div className="overview-card-content">
                          <div className="overview-card-text">
                            <div className="overview-card-label">
                              Total Tests
                            </div>
                            <strong>
                              {studentData?.overview?.total_tests ?? 0}
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div className="card overview-card">
                        <div className="overview-card-content">
                          <div className="overview-card-text">
                            <div className="overview-card-label">
                              Tests Completed
                            </div>
                            <strong>
                              {studentData?.overview?.tests_completed ?? 0}
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div className="card overview-card">
                        <div className="overview-card-content">
                          <div className="overview-card-text">
                            <div className="overview-card-label">
                              Average Score
                            </div>
                            <strong>
                              {studentData?.overview?.average_score ?? 0}
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div className="card overview-card">
                        <div className="overview-card-content">
                          <div className="overview-card-text">
                            <div className="overview-card-label">
                              Homework Completed
                            </div>
                            <strong>
                              {studentData?.overview?.homework_completed ?? 0}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {studentAnalytics && (
                    <section className="dashboard-course-section">
                      <h3>Practice Activities</h3>
                      <div className="dashboard-course-grid">
                        <ChartCard title="Active Recall Overview">
                          <DoughnutChart
                            data={{
                              labels: ["Completed", "Remaining"],
                              datasets: [
                                {
                                  data: (() => {
                                    const overall =
                                      studentAnalytics.active_recall_mastery
                                        ?.overall || {};
                                    const total =
                                      Number(overall.total_recall) || 0;
                                    const completed =
                                      Number(overall.total_recall_completed) ||
                                      0;
                                    const remaining = Math.max(
                                      0,
                                      total - completed,
                                    );
                                    return [completed, remaining];
                                  })(),
                                  backgroundColor: [
                                    chartPalette.solid.lime,
                                    chartPalette.solid.slate,
                                  ],
                                  hoverBackgroundColor: [
                                    chartPalette.hover.lime,
                                    chartPalette.hover.slate,
                                  ],
                                  borderColor: "rgba(15, 23, 42, 1)",
                                  borderWidth: 0,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              plugins: { legend: { position: "bottom" } },
                            }}
                          />
                        </ChartCard>

                        <ChartCard title="Adaptive Learning Overview">
                          <BarChart
                            data={{
                              labels: ["Total Sessions", "Completed"],
                              datasets: [
                                {
                                  label: "Adaptive",
                                  data: (() => {
                                    const overall =
                                      studentAnalytics.adaptive_learning_mastery
                                        ?.overall || {};
                                    const total =
                                      Number(overall.total_adaptive_learning) ||
                                      0;
                                    const completed =
                                      Number(
                                        overall.total_adaptive_learning_completed,
                                      ) || 0;
                                    return [total, completed];
                                  })(),
                                  backgroundColor: [
                                    chartPalette.solid.mint,
                                    chartPalette.solid.yellow,
                                  ],
                                  hoverBackgroundColor: [
                                    chartPalette.hover.mint,
                                    chartPalette.hover.yellow,
                                  ],
                                  borderRadius: 10,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              plugins: { legend: { position: "bottom" } },
                              scales: { y: { beginAtZero: true } },
                            }}
                          />
                        </ChartCard>
                      </div>
                    </section>
                  )}

                  {studentAnalytics && (
                    <section className="dashboard-course-section">
                      <h3>Assessments</h3>
                      <div className="dashboard-course-grid">
                        <ChartCard title="Quizzes Overview">
                          <BarChart
                            data={{
                              labels: ["Total Quizzes", "Completed"],
                              datasets: [
                                {
                                  label: "Quizzes",
                                  data: (() => {
                                    const overall =
                                      studentAnalytics.quiz_completed_mastery
                                        ?.overall || {};
                                    const total =
                                      Number(overall.total_quiz) || 0;
                                    const completed =
                                      Number(overall.total_quiz_completed) || 0;
                                    return [total, completed];
                                  })(),
                                  backgroundColor: [
                                    chartPalette.solid.orange,
                                    chartPalette.solid.yellow,
                                  ],
                                  hoverBackgroundColor: [
                                    chartPalette.hover.orange,
                                    chartPalette.hover.yellow,
                                  ],
                                  borderRadius: 10,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              plugins: { legend: { position: "bottom" } },
                              scales: { y: { beginAtZero: true } },
                            }}
                          />
                        </ChartCard>

                        <ChartCard title="Tests Overview">
                          <LineChart
                            data={{
                              labels: ["Total", "Completed"],
                              datasets: [
                                {
                                  label: "Tests",
                                  data: (() => {
                                    const overall =
                                      studentAnalytics.test_completed_mastery
                                        ?.overall || {};
                                    const total =
                                      Number(overall.total_test) || 0;
                                    const completed =
                                      Number(overall.total_test_completed) || 0;
                                    return [total, completed];
                                  })(),
                                  borderColor: chartPalette.solid.mint,
                                  backgroundColor: chartPalette.soft.mint,
                                  pointBackgroundColor: chartPalette.solid.mint,
                                  pointHoverBackgroundColor:
                                    chartPalette.hover.mint,
                                  pointBorderColor: "rgba(15, 23, 42, 1)",
                                  pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              plugins: { legend: { position: "bottom" } },
                              scales: { y: { beginAtZero: true } },
                            }}
                          />
                        </ChartCard>
                      </div>
                    </section>
                  )}

                  {studentAnalytics &&
                    Array.isArray(studentAnalytics.year_time_spent) &&
                    studentAnalytics.year_time_spent.length > 0 && (
                      <section className="dashboard-course-section">
                        <h3>Engagement Over Time</h3>
                        <div className="dashboard-course-grid">
                          <ChartCard title="Time Spent Over Time">
                            <LineChart
                              data={{
                                labels: studentAnalytics.year_time_spent.map(
                                  (p) => p.date || "",
                                ),
                                datasets: [
                                  {
                                    label: "Minutes Spent",
                                    data: studentAnalytics.year_time_spent.map(
                                      (p) => p.time_spent || 0,
                                    ),
                                    borderColor: chartPalette.solid.lime,
                                    backgroundColor: chartPalette.soft.lime,
                                    pointBackgroundColor:
                                      chartPalette.solid.lime,
                                    pointHoverBackgroundColor:
                                      chartPalette.hover.lime,
                                    pointBorderColor: "rgba(15, 23, 42, 1)",
                                    pointHoverBorderColor:
                                      "rgba(15, 23, 42, 1)",
                                    tension: 0.35,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: { legend: { position: "bottom" } },
                                scales: { y: { beginAtZero: true } },
                              }}
                            />
                          </ChartCard>

                          <ChartCard title="Time Spent by Subject">
                            <BarChart
                              data={{
                                labels: buildSubjectList(
                                  studentAnalytics.total_time_spent,
                                ).map((s) => s.name),
                                datasets: [
                                  {
                                    label: "Total Minutes",
                                    data: buildSubjectList(
                                      studentAnalytics.total_time_spent,
                                    ).map((s) => {
                                      const arr = Array.isArray(s.time_spent)
                                        ? s.time_spent
                                        : [];
                                      return arr.reduce(
                                        (sum, item) =>
                                          sum + (Number(item.time_spent) || 0),
                                        0,
                                      );
                                    }),
                                    backgroundColor: chartPalette.solid.orange,
                                    hoverBackgroundColor:
                                      chartPalette.hover.orange,
                                    borderRadius: 10,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: { legend: { position: "bottom" } },
                                scales: { y: { beginAtZero: true } },
                              }}
                            />
                          </ChartCard>
                        </div>
                      </section>
                    )}
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;
